// W01 / M01 — Belépés.
//
// A tervek szerinti kétoszlopos elrendezés: bal oldalt akcentus-panel a
// márkaüzenettel, jobb oldalt a 420 px-es űrlap. Mobilon a bal panel elmarad,
// és az űrlap tölti ki a képernyőt.

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type Auth,
} from 'firebase/auth';
import { t } from '../lib/i18n';
import { h, icon, icons } from '../lib/dom';

/// A Firebase hibakódjainak szótárbeli megfelelője.
///
/// A nyers kódot ("auth/invalid-credential") sosem mutatjuk a felhasználónak.
function errorMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-email':
      return t('auth.invalidEmail');
    case 'auth/user-not-found':
      return t('auth.noUserWithEmail');
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return t('auth.wrongCredentials');
    case 'auth/too-many-requests':
      return t('auth.tooManyRequests');
    case 'auth/network-request-failed':
      return t('auth.networkError');
    default:
      return t('auth.loginUnknownError');
  }
}

export function loginPage(
  auth: Auth,
  options?: { onRegister?: () => void },
): HTMLElement {
  const emailInput = h('input', {
    class: 'input',
    type: 'email',
    id: 'login-email',
    autocomplete: 'username',
    placeholder: t('auth.emailPlaceholder'),
    required: 'true',
  });

  const passwordInput = h('input', {
    class: 'input',
    type: 'password',
    id: 'login-password',
    autocomplete: 'current-password',
    placeholder: t('auth.passwordPlaceholder'),
    required: 'true',
    // Helyet hagyunk a szem-gombnak, hogy ne takarja a beírt jelszót.
    style: { paddingRight: '44px' },
  });

  /// A terv szem-gombja a mezőben.
  ///
  /// Enélkül egy elgépelt jelszót csak találgatni lehetett: a mező csupa
  /// pontot mutat, a hibaüzenet pedig csak annyit mond, hogy „hibás e-mail
  /// cím vagy jelszó".
  const revealButton = h(
    'button',
    {
      class: 'btn btn-ghost btn-icon btn-sm',
      type: 'button',
      'aria-label': t('auth.showPassword'),
      'aria-pressed': 'false',
      style: { position: 'absolute', right: '6px', top: '6px' },
      onClick: () => {
        const shown = passwordInput.type === 'text';
        passwordInput.type = shown ? 'password' : 'text';
        revealButton.setAttribute('aria-pressed', shown ? 'false' : 'true');
        revealButton.setAttribute(
          'aria-label',
          shown ? 'Jelszó megjelenítése' : 'Jelszó elrejtése',
        );
        passwordInput.focus();
      },
    },
    icon(icons.eye),
  );

  /// „Maradjak bejelentkezve" — a Firebase munkamenet-tárolását állítja.
  ///
  /// Bekapcsolva a belépés túlél egy böngésző-újraindítást (`local`),
  /// kikapcsolva csak a fület (`session`). Közös gépen ez nem mindegy, és
  /// eddig nem lehetett választani: a Firebase alapértelmezése a `local`.
  const rememberInput = h('input', {
    type: 'checkbox',
    id: 'login-remember',
    checked: 'true',
    style: { width: '16px', height: '16px', accentColor: 'var(--accent)' },
  });

  const errorBox = h('div', {
    class: 'row top g12',
    role: 'alert',
    style: {
      display: 'none',
      padding: '12px 14px',
      borderRadius: '10px',
      background: 'var(--err-soft)',
      border: '1px solid var(--s-rej-bd)',
    },
  });

  function showError(message: string): void {
    errorBox.replaceChildren(
      h('span', { class: 'ic', style: { color: 'var(--err)' } }, icon(icons.warning)),
      h('span', { class: 'body-sm w6', style: { color: 'var(--err)' }, text: message }),
    );
    errorBox.style.display = 'flex';
    // A hibaüzenetre fókuszálunk, hogy a képernyőolvasó is felolvassa.
    errorBox.setAttribute('tabindex', '-1');
    errorBox.focus();
  }

  function showInfo(message: string): void {
    errorBox.replaceChildren(
      h('span', { class: 'body-sm w6', style: { color: 'var(--ok)' }, text: message }),
    );
    errorBox.style.background = 'var(--ok-soft)';
    errorBox.style.borderColor = 'var(--s-ok-bd)';
    errorBox.style.display = 'flex';
  }

  function hideError(): void {
    errorBox.style.display = 'none';
  }

  const submit = h('button', {
    class: 'btn btn-primary btn-lg',
    type: 'submit',
    style: { width: '100%' },
    text: t('auth.login'),
  });

  /// A jelszó-emlékeztető. A terv szerint a jelszómező ALATT, a „maradjak
  /// bejelentkezve" mellett áll — nem teljes szélességű gombként a belépés
  /// alatt, ahol ugyanakkora súlyt kapott, mint maga a belépés.
  const forgotLink = h('button', {
    class: 'btn btn-ghost btn-sm',
    type: 'button',
    text: t('auth.forgotPassword'),
    onClick: async () => {
      const email = emailInput.value.trim();
      if (!email) {
        showError(t('auth.provideEmailForReset'));
        emailInput.focus();
        return;
      }
      try {
        await sendPasswordResetEmail(auth, email);
        showInfo(t('auth.resetSentInline'));
      } catch (error) {
        showError(errorMessage((error as { code?: string }).code ?? ''));
      }
    },
  });

  let busy = false;
  function setBusy(value: boolean): void {
    busy = value;
    submit.disabled = value;
    submit.textContent = value ? t('auth.loggingIn') : t('auth.login');
  }

  const form = h(
    'form',
    {
      class: 'col g16',
      novalidate: 'true',
      onSubmit: async (event: Event) => {
        event.preventDefault();
        if (busy) return;
        hideError();

        const email = emailInput.value.trim();
        const password = passwordInput.value;

        if (!email || !password) {
          showError(t('auth.provideCredentials'));
          return;
        }

        setBusy(true);
        try {
          // A tárolás VÁLASZTÁSA a belépés előtt kell: a most létrejövő
          // munkamenet különben még a korábbi szabály szerint tárolódna.
          await setPersistence(
            auth,
            rememberInput.checked
              ? browserLocalPersistence
              : browserSessionPersistence,
          );
          await signInWithEmailAndPassword(auth, email, password);
          // A továbblépésről az auth-figyelő gondoskodik (main.ts).
        } catch (error) {
          const code = (error as { code?: string }).code ?? '';
          showError(errorMessage(code));
        } finally {
          setBusy(false);
        }
      },
    },
    h(
      'div',
      { class: 'field' },
      h('label', { class: 'flbl', for: 'login-email', text: t('auth.email') }),
      emailInput,
    ),
    h(
      'div',
      { class: 'field' },
      h('label', { class: 'flbl', for: 'login-password', text: t('auth.password') }),
      h('div', { style: { position: 'relative' } }, passwordInput, revealButton),
      h(
        'div',
        {
          class: 'row',
          style: { justifyContent: 'space-between', alignItems: 'center' },
        },
        h(
          'label',
          {
            class: 'row g8',
            for: 'login-remember',
            style: { alignItems: 'center', cursor: 'pointer' },
          },
          rememberInput,
          h('span', { class: 'body-sm', text: t('auth.rememberMe') }),
        ),
        forgotLink,
      ),
    ),
    submit,
  );

  const brandPanel = h(
    'aside',
    {
      class: 'brand-panel',
      style: {
        background: 'var(--accent)',
        color: 'var(--on-accent)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px',
      },
    },
    h(
      'span',
      { class: 'logo', style: { fontSize: '18px', color: 'var(--on-accent)' } },
      h(
        'span',
        { class: 'mk', style: { background: 'rgba(255,255,255,.18)' } },
        icon(icons.spark),
      ),
      'Blaze',
    ),
    h(
      'div',
      { class: 'col', style: { gap: '18px' } },
      h('h2', {
        class: 'display',
        style: {
          fontSize: '44px',
          color: 'var(--on-accent)',
          whiteSpace: 'pre-line',
        },
        text: t('auth.brandHeadline'),
      }),
      h('p', {
        class: 'body-lg',
        style: { maxWidth: '400px', opacity: '.92' },
        text: t('auth.brandLead'),
      }),
    ),
    h(
      'div',
      { class: 'row g12', style: { opacity: '.9' } },
      h('span', { class: 'ic' }, icon(icons.shield)),
      h('span', {
        class: 'body-sm',
        text: t('auth.brandOffline'),
      }),
    ),
  );

  return h(
    'div',
    { class: 'login-layout' },
    brandPanel,
    h(
      'main',
      {
        style: {
          flexGrow: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 16px',
        },
      },
      h(
        'div',
        { class: 'col', style: { width: '420px', maxWidth: '100%', gap: '22px' } },
        h(
          'div',
          { class: 'col', style: { gap: '6px' } },
          h('h1', { class: 'h1', text: t('auth.loginTitle') }),
          h('p', {
            class: 'body ts',
            text: t('auth.loginSubtitle'),
          }),
        ),
        errorBox,
        form,
        // A regisztracio csak akkor jelenik meg, ha a hivo ad ra utvonalat —
        // igy a bejelentkezo kepernyo onmagaban is hasznalhato marad (pl. a
        // fejlesztoi elonezetben), halott gomb nelkul.
        ...(options?.onRegister
          ? [
              h(
                'div',
                { class: 'row g6', style: { justifyContent: 'center' } },
                h('span', { class: 'body-sm ts', text: t('auth.noAccountQuestion') }),
                h('button', {
                  class: 'btn btn-ghost btn-sm',
                  type: 'button',
                  text: t('auth.register'),
                  onClick: options.onRegister,
                }),
              ),
            ]
          : [
              // Regisztrációs út nélkül a terv zárómondata áll itt: megmondja,
              // hogyan jut be valaki, aki még nem tud belépni. Korábban ilyen
              // esetben semmi nem állt a gomb alatt.
              h('p', {
                class: 'caption',
                style: { textAlign: 'center' },
                text: t('auth.noAccountHint'),
              }),
            ]),
      ),
    ),
  );
}
